import { ProfileSection } from 'features/customerPanel/cpPlayerProfile/interface/interface';
import { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { APP_URL } from 'shared/constants/common';
import usePolicyData from 'shared/hooks/usePolicyData';
import { CategoryDataInterface, State } from 'shared/interface';
import { setStaticBreadcrumbs } from 'shared/util/breadcrumbsSwitch';
import { generateUrl } from 'shared/util/generateUrl';
import { isMobile } from 'shared/util/utility';
import { useSpecialCategoryNameFeatureFlag } from '../../featureFlags';
import { useEventStartFilters } from '../../hooks/useEventStartFilters';
import { BreadcrumbsData } from '../../types/cms';
import BreadcrumbsDesktop from './breadcrumbsDesktop';
import BreadcrumbsMobile from './breadcrumbsMobile';

interface BreadcrumbsProps {
	className?: string;
}

export type BreadcrumbsElement = BreadcrumbsData;

const Breadcrumbs = (props: BreadcrumbsProps) => {
	const history = useHistory();
	const location = useLocation<BreadcrumbsElement['state']>();
	const lastItem = parseInt(location.pathname.substring(location.pathname.lastIndexOf('/') + 1));
	const policyData = usePolicyData(lastItem.toString());
	const categories = useSelector((state: State) => state.categories.all);
	const eventDetails = useSelector((state: State) => state.eventPreMatchData.event);
	const eventLiveDetails = useSelector((state: State) => state.eventLiveData.liveDetails);
	const promotionsDetails = useSelector((state: State) => state.promotionDetails);
	const { currentSearchParamsFilterValue } = useEventStartFilters();

	const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbsElement[]>([]);
	const isEsport = location.pathname.includes(APP_URL.eSport);

	const specialCategoryNameFeatureFlag = useSpecialCategoryNameFeatureFlag();

	const navBack = () => {
		history.goBack();
	};

	const checkDisableNoFollow = (category: CategoryDataInterface): boolean => {
		if (category.disableNoFollow !== undefined) {
			return category.disableNoFollow;
		}

		if (!category.parentCategory || !categories[category.parentCategory]) {
			return false;
		}

		// Recursively go up the category tree to find first parent with defined disableNoFollow.
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		return checkDisableNoFollow(categories[category.parentCategory]!);
	};

	const generateBreadcrumbs = useCallback(
		(matchedCategoryElem: CategoryDataInterface, breadcrumbsFromId: BreadcrumbsElement[]) => {
			if (matchedCategoryElem && matchedCategoryElem.parentCategory !== 0) {
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				const parent = categories[matchedCategoryElem.parentCategory]!;
				const pathState = generateUrl(parent);
				const breadcrumb: BreadcrumbsElement = {
					label: parent?.categoryName || '',
					value: pathState.pathname,
					state: pathState.state,
					disableNoFollow: checkDisableNoFollow(parent),
					filter: currentSearchParamsFilterValue,
				};
				breadcrumbsFromId.push(breadcrumb);
				generateBreadcrumbs(parent, breadcrumbsFromId);
			}
			return breadcrumbsFromId;
		},
		[categories, policyData],
	);

	const staticLabelAndValue = useCallback((breadcrumbsLastElem: BreadcrumbsElement, name: string, path: string) => {
		breadcrumbsLastElem.label = name;
		breadcrumbsLastElem.value = path;
	}, []);

	const setLastCategoryFromEventId = useCallback((breadcrumbsArray: Array<BreadcrumbsElement>, lastCategory: BreadcrumbsElement) => {
		breadcrumbsArray.splice(breadcrumbsArray.length - 1, 0, lastCategory);
	}, []);

	const getBreadcrumbs = useCallback(() => {
		const matchedCategoryElem = categories[lastItem];
		const matchedFromEvent = categories[eventDetails?.categoryId];

		// const matchedFromEventLive = categories[eventLiveDetails?.categoryId];
		const newBreadcrumbs: Array<BreadcrumbsElement> = [];

		const breadcrumbsFromId: BreadcrumbsElement[] = [];

		const homeBreadcrumb: BreadcrumbsElement = {
			label: t('home'),
			value: APP_URL.home,
			state: {
				sportId: 0,
				sportName: '',
			},
			disableNoFollow: true,
		};
		const breadcrumbsLastElem: BreadcrumbsElement = {
			label: '',
			value: '',
			state: {
				sportId: 0,
				sportName: '',
			},
			filter: currentSearchParamsFilterValue,
		};
		const breadcrumbEvent: BreadcrumbsElement = {
			label: '',
			value: '',
			state: {
				sportId: 0,
				sportName: '',
			},
			filter: currentSearchParamsFilterValue,
		};
		const breadcrumbEventLastCategory: BreadcrumbsElement = {
			label: '',
			value: '',
			state: {
				sportId: 0,
				sportName: '',
			},
			filter: currentSearchParamsFilterValue,
		};

		if (matchedCategoryElem) {
			const breadcrumbSport: BreadcrumbsElement = {
				label: isEsport ? t('eSport') : t('offer'),
				value: isEsport ? APP_URL.eSport : APP_URL.sport,
				state: {
					sportId: 0,
					sportName: '',
				},
				disableNoFollow: true,
				filter: currentSearchParamsFilterValue,
			};

			breadcrumbsLastElem.label = policyData.name ? policyData.name : matchedCategoryElem.categoryName || '';
			breadcrumbsLastElem.disableNoFollow = matchedCategoryElem.disableNoFollow;
			newBreadcrumbs.push(breadcrumbsLastElem);
			const breadcrumbsElements = generateBreadcrumbs(matchedCategoryElem, breadcrumbsFromId);
			breadcrumbsElements.forEach((el: BreadcrumbsElement) => {
				newBreadcrumbs.unshift(el);
			});
			newBreadcrumbs.splice(0, 0, breadcrumbSport);
		} else if (!matchedCategoryElem && !matchedFromEvent && location.pathname.includes(APP_URL.sport) && !(location.pathname === APP_URL.sport)) {
			const breadcrumbSportName = {
				label: location.state?.sportName === undefined ? policyData.name : location.state?.sportName,
				value: '',
				state: {
					sportId: 0,
					sportName: '',
				},
				disableNoFollow: false,
				filter: currentSearchParamsFilterValue,
			};
			staticLabelAndValue(breadcrumbsLastElem, t('offer'), APP_URL.sport);
			breadcrumbsLastElem.disableNoFollow = true;

			newBreadcrumbs.push(breadcrumbsLastElem);
			newBreadcrumbs.push(breadcrumbSportName);
		} else if (!matchedCategoryElem && matchedFromEvent && location.pathname.includes(eventDetails.eventId.toString())) {
			const matched = generateUrl(matchedFromEvent);
			const breadcrumbsGenerate = generateBreadcrumbs(matchedFromEvent, breadcrumbsFromId);
			breadcrumbsGenerate.forEach((el: BreadcrumbsElement) => {
				newBreadcrumbs.unshift(el);
			});
			breadcrumbEvent.label = eventDetails.eventName || '';
			breadcrumbEvent.disableNoFollow = eventDetails.disableNoFollow;
			if (isEsport) {
				staticLabelAndValue(breadcrumbsLastElem, t('esport'), APP_URL.eSport);
			} else {
				staticLabelAndValue(breadcrumbsLastElem, t('offer'), APP_URL.sport);
			}
			breadcrumbsLastElem.disableNoFollow = true;

			breadcrumbEventLastCategory.label = eventDetails.categoryName || '';
			breadcrumbEventLastCategory.value = matched.pathname;
			breadcrumbEventLastCategory.state = matched.state;
			breadcrumbEventLastCategory.disableNoFollow = matchedFromEvent?.disableNoFollow || false;

			newBreadcrumbs.push(breadcrumbEvent);
			newBreadcrumbs.unshift(breadcrumbsLastElem);
			setLastCategoryFromEventId(newBreadcrumbs, breadcrumbEventLastCategory);
		} else if (location.pathname.includes(APP_URL.live) && location.pathname !== APP_URL.live) {
			if (location.pathname.includes(APP_URL.live)) {
				staticLabelAndValue(breadcrumbsLastElem, t('live'), APP_URL.live);
				breadcrumbsLastElem.disableNoFollow = true;
			}

			breadcrumbEvent.label = eventLiveDetails?.eventName || '';
			breadcrumbEvent.disableNoFollow = eventLiveDetails.disableNoFollow;

			newBreadcrumbs.push(breadcrumbEvent);
			newBreadcrumbs.unshift(breadcrumbsLastElem);
		} else if (location.pathname.includes(APP_URL.promotion) && promotionsDetails && location.pathname !== APP_URL.promotion) {
			staticLabelAndValue(breadcrumbsLastElem, t('promotion'), APP_URL.promotion);
			breadcrumbsLastElem.disableNoFollow = true;

			breadcrumbEvent.label = promotionsDetails.title || '';

			newBreadcrumbs.push(breadcrumbsLastElem);
			newBreadcrumbs.push(breadcrumbEvent);
		} else if (location.pathname.includes(APP_URL.horseRaceGame)) {
			breadcrumbEvent.label = t('horseRaceGame') || '';
			newBreadcrumbs.push(breadcrumbEvent);
		} else if (location.pathname.includes('kupon')) {
			// staticLabelAndValue(breadcrumbsLastElem, t('kupon'), APP_URL.coupon);
			breadcrumbEvent.label = t('coupons') || '';
			newBreadcrumbs.push(breadcrumbEvent);
		} else if (location.pathname.includes(APP_URL.entertainment)) {
			breadcrumbEvent.label = t('entertainment') || '';
			newBreadcrumbs.push(breadcrumbEvent);
		} else if (location.pathname.includes(APP_URL.special)) {
			breadcrumbEvent.label = specialCategoryNameFeatureFlag.value ?? 'Specjalne';
			newBreadcrumbs.push(breadcrumbEvent);
		} else if (isMobile() && location.pathname.includes(APP_URL.playerProfile)) {
			breadcrumbEvent.label = t('playerProfile') || '';
			newBreadcrumbs.push(breadcrumbEvent);
			breadcrumbsLastElem.label = t('playerProfile');
			breadcrumbsLastElem.value = APP_URL.playerProfile;
			if (location.pathname.includes(ProfileSection.MEDAL)) {
				breadcrumbEvent.label = t('medalProfileTitle') || '';
				newBreadcrumbs.unshift(breadcrumbsLastElem);
			}
		}

		setStaticBreadcrumbs(breadcrumbsLastElem, newBreadcrumbs, location.pathname);

		newBreadcrumbs.unshift(homeBreadcrumb);
		setBreadcrumbs(newBreadcrumbs);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		categories,
		eventDetails,
		// eventLiveDetails?.categoryId,
		// eventLiveDetails?.categoryName,
		eventLiveDetails?.eventName,
		generateBreadcrumbs,
		location.pathname,
		promotionsDetails,
		location.state?.sportName,
		setLastCategoryFromEventId,
		staticLabelAndValue,
	]);

	useEffect(() => {
		getBreadcrumbs();

		const breadcrumbScrollWrapper = document.getElementById('breadcrumb-scroll-wrapper');
		breadcrumbScrollWrapper?.scrollTo(breadcrumbScrollWrapper.scrollWidth, 0);
	}, [location, eventDetails, eventLiveDetails, categories, promotionsDetails, getBreadcrumbs]);

	if (breadcrumbs.length === 0) {
		return <></>;
	}

	return (
		<div
			className={`breadcrumb__wrapper_custom ${props.className ? props.className : ''} ${
				isMobile() ? 'breadcrumb-fixed' : `desktop-breadcrumbs`
			}`}
		>
			{isMobile() ? (
				<BreadcrumbsMobile breadcrumbs={breadcrumbs} navBack={navBack} />
			) : (
				<BreadcrumbsDesktop breadcrumbs={breadcrumbs} navBack={navBack} />
			)}
		</div>
	);
};

export default Breadcrumbs;
